import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "itdesk@afriportb2c.online";

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const snap = await getDoc(doc(db, "users", firebaseUser.uid));
        if (snap.exists()) setProfile(snap.data());
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  async function register(email, password, data) {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: data.fullName });
    const isAdmin = email === ADMIN_EMAIL;
    const role = isAdmin ? "admin" : data.accountType;
    const profileData = {
      uid: cred.user.uid,
      email,
      fullName: data.fullName,
      phone: data.phone || "",
      role,
      createdAt: serverTimestamp(),
      ...(role === "business" && {
        businessName: data.businessName || "",
        businessAddress: data.businessAddress || "",
        bankName: data.bankName || "",
        accountNumber: data.accountNumber || "",
        accountName: data.accountName || "",
        commissionRate: 10,
        payoutRate: 90,
        businessApproved: false,
      }),
    };
    await setDoc(doc(db, "users", cred.user.uid), profileData);
    setProfile(profileData);
    return cred;
  }

  async function login(email, password) {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    let snap = await getDoc(doc(db, "users", cred.user.uid));
    let profileData = snap.exists() ? snap.data() : null;
    
    // Check if user should be admin based on email
    const isAdmin = email === ADMIN_EMAIL;
    
    // If profile exists and isn't admin but email matches admin email, update to admin
    if (profileData && !profileData.role && isAdmin) {
      profileData.role = "admin";
      await setDoc(doc(db, "users", cred.user.uid), profileData, { merge: true });
    }
    
    // If profile doesn't exist but email is admin email, create admin profile
    if (!profileData && isAdmin) {
      profileData = {
        uid: cred.user.uid,
        email,
        fullName: cred.user.displayName || "Admin",
        phone: "",
        role: "admin",
        createdAt: serverTimestamp(),
      };
      await setDoc(doc(db, "users", cred.user.uid), profileData);
    }
    
    if (profileData) setProfile(profileData);
    
    // Send login notification email
    try {
      const sendLoginEmail = httpsCallable(functions, "onUserLogin");
      await sendLoginEmail({ email, fullName: profileData?.fullName || cred.user.displayName || "User" });
    } catch (err) {
      console.warn("Could not send login email:", err.message);
    }
    
    return cred;
  }

  async function logout() {
    await signOut(auth);
    setUser(null);
    setProfile(null);
  }

  const isAdmin = profile?.role === "admin";
  const isBusiness = profile?.role === "business";
  const isCustomer = profile?.role === "customer";
  const isWebAdmin = profile?.role === "web_admin";
  const isAccounts = profile?.role === "accounts";
  // Any staff-level access (can see admin dashboard)
  const isStaff = isAdmin || isWebAdmin || isAccounts;

  return (
    <AuthContext.Provider
      value={{ user, profile, loading, register, login, logout, isAdmin, isBusiness, isCustomer, isWebAdmin, isAccounts, isStaff }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
